import type { ViewBody } from '@ayumia/contracts';

/**
 * Corps de vue de la médiathèque (ADR-149 §6) : la médiathèque est une **vue** sur le composant de
 * collection, en présentation drive, dont la source est la requête déclarée `ListMedia` (adaptateur de
 * source : une lecture gouvernée, ni base ni cache). Les dossiers restent `MediaFolder`, les fichiers
 * `File`, les commandes celles d'ADR-106. Tranche 1 : écrits ici et validés par le contrat `view-body`
 * en test ; tranche 2 : artefacts `view` du Core, lus par `GetView` (aucune copie ne restera ici).
 */

export type Library = 'images' | 'documents';

const fr = (value: string) => ({ fr: value });

const folders = (library: Library) => ({
  presentation: 'drive' as const,
  folder_object_type: 'MediaFolder',
  parent_field: 'parent_id',
  item_folder_field: 'folder_id',
  name_field: 'name',
  locked_field: 'system_key',
  readonly_field: 'readonly',
  scope_filter: { library: { eq: library } },
  listing_query: {
    query_name: 'ListMedia',
    items_key: 'files',
    parameters: { library },
    filter_parameters: ['format', 'tag'],
    sort_options: [
      { value: 'name', label: fr('Nom ↑') },
      { value: '-name', label: fr('Nom ↓') },
      { value: '-date', label: fr('Date ↓') },
      { value: 'date', label: fr('Date ↑') },
      { value: '-size', label: fr('Taille ↓') },
      { value: 'size', label: fr('Taille ↑') },
    ],
    default_sort: library === 'images' ? 'name' : '-date',
    page_size: 200,
  },
  commands: {
    create: { command_name: 'CreateMediaFolder', name_field: 'name', parent_field: 'parent_id', payload: { library } },
    rename: { command_name: 'RenameMediaFolder', name_field: 'name' },
    move: { command_name: 'MoveMediaFolder', parent_field: 'parent_id' },
    archive: { command_name: 'DeleteMediaFolder' },
    move_items: { command_name: 'MoveFiles', ids_field: 'file_ids', folder_field: 'folder_id' },
  },
  search_scope: { default: 'everywhere' as const, choice: true },
});

const bulk = [
  { command_name: 'MoveFiles', label: fr('Déplacer vers…'), icon: 'move', min_selection: 1, max_selection: 500, applies_to: 'all' as const },
  { command_name: 'ArchiveFiles', label: fr('Archiver'), icon: 'archive', min_selection: 1, max_selection: 500, variant: 'danger' as const, confirm: { message: fr('Les éléments choisis sont archivés, jamais effacés ici ; les liens vers ces fichiers restent valides.') }, applies_to: 'all' as const },
];

const shortcuts = (library: Library) => [
  { shortcut_id: 'all', label: fr(library === 'images' ? 'Toutes les images' : 'Tous les documents'), icon: library === 'images' ? 'image' : 'file' },
  { shortcut_id: 'recent', label: fr(library === 'images' ? 'Récentes' : 'Récents'), icon: 'clock', sort: '-date', everywhere: true },
  ...(library === 'documents'
    ? [
        { shortcut_id: 'pdf', label: fr('PDF'), icon: 'file', filter: { format: { eq: 'pdf' } }, everywhere: true },
        { shortcut_id: 'office', label: fr('Office'), icon: 'file', filter: { format: { eq: 'office' } }, everywhere: true },
        { shortcut_id: 'photos', label: fr('Photos'), icon: 'image', filter: { format: { eq: 'image' } }, everywhere: true },
      ]
    : []),
  library === 'images'
    ? { shortcut_id: 'documents', label: fr('Documents'), icon: 'file', workspace_ref: 'core.workspace.media_documents' }
    : { shortcut_id: 'images', label: fr('Images'), icon: 'image', workspace_ref: 'core.workspace.media_images' },
];

const chips = (library: Library) =>
  library === 'images'
    ? [{ field: 'tag', label: fr('Étiquettes'), options_query: { query_name: 'ListMediaTags', parameters: { library }, list_key: 'tags', value_key: 'tag', count_key: 'count' } }]
    : [
        { field: 'format', label: fr('Type'), options: [{ value: 'pdf', label: fr('PDF') }, { value: 'office', label: fr('Office') }, { value: 'image', label: fr('Photos') }, { value: 'other', label: fr('Autres') }] },
        { field: 'tag', label: fr('Étiquettes'), options_query: { query_name: 'ListMediaTags', parameters: { library }, list_key: 'tags', value_key: 'tag', count_key: 'count' } },
      ];

const columns = (library: Library) =>
  library === 'images'
    ? [
        { field: 'dimensions', label: fr('Dimensions') },
        { field: 'size_bytes', label: fr('Taille'), align: 'end' as const },
        { field: 'tags', label: fr('Étiquettes') },
        { field: 'created_at', label: fr('Déposée le') },
      ]
    : [
        { field: 'format', label: fr('Format') },
        { field: 'page_count', label: fr('Pages'), align: 'end' as const },
        { field: 'size_bytes', label: fr('Taille'), align: 'end' as const },
        { field: 'created_at', label: fr('Déposé le') },
      ];

export function mediaView(library: Library): ViewBody {
  const body = {
    entry: library === 'images' ? 'grid' : 'list',
    presentation: 'page',
    title: fr(library === 'images' ? 'Images' : 'Documents'),
    target_object_type: 'File',
    data_source: { kind: 'query', query_name: 'ListMedia', page_size: 200 },
    collection: {
      inputs: {
        display: { remember: true },
        selection: { range: true, keyboard: true, click_selects: true },
        bulk_commands: bulk,
        folders: folders(library),
        drive: { new_menu: { create_folder: true, upload: true }, shortcuts: shortcuts(library), filter_chips: chips(library), info_panel: { activity: false } },
      },
    },
    representations: [
      { id: 'list', component: 'Table', label: fr('Liste'), inputs: { columns: columns(library), selectable: true } },
      { id: 'grid', component: 'CardList', label: fr('Grille'), inputs: { card: { zones: [{ type: 'identity', title_field: 'title', icon: 'file' }] }, selectable: true } },
    ],
  };
  return body as unknown as ViewBody;
}
